import { ChatIcon, ComponentRadioGroup, FormLabel, LeftPanelIcon, RightPanelIcon, WysiwygEditor } from "shared";
import {
    ITemplateComponent,
    ITemplateExternalFormComponent,
    stringToWysiwygSettings
} from "entities/template";
import React, { useEffect, useState } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import styles from "./component_external_form.module.scss";
import { WysiwygDisplayVariant } from "../../../../lib/types/wysiwyg-display-variant";
import { ComponentTooltip } from "../../../document_form/components/component_tooltip/component_tooltip";

interface IComponentExternalFormProps {
    component: ITemplateExternalFormComponent;
    componentList: ITemplateComponent[];
    enLanguageEnabled: boolean;
    onChange: (component: ITemplateComponent) => void;

}

export const ComponentExternalForm: React.FC<IComponentExternalFormProps> = React.memo(({
                                                                                            component,
                                                                                            enLanguageEnabled,
                                                                                            componentList,
                                                                                            onChange
                                                                                        }) => {
    const getDisplayVariant = (tooltipVal: string | undefined) => {
        const displayVariant = stringToWysiwygSettings(tooltipVal).wysiwygDisplayVariant;
        return displayVariant ? displayVariant : WysiwygDisplayVariant.tooltip;
    };

    const t = useComponentsTranslations();
    const [wysiwygDisplayVariant, setWysiwygDisplayVariant] = useState<WysiwygDisplayVariant>(getDisplayVariant(component.wysiwyg_tooltip));

    useEffect(() => {
        const wysiwyg_tooltip = JSON.stringify({
            ...stringToWysiwygSettings(component.wysiwyg_tooltip),
            wysiwygDisplayVariant
        });
        let changes: Partial<ITemplateExternalFormComponent> = { wysiwyg_tooltip };
        if (enLanguageEnabled) {
            const wysiwyg_tooltip_en = JSON.stringify({
                ...stringToWysiwygSettings(component.wysiwyg_tooltip_en),
                wysiwygDisplayVariant
            });
            changes = { ...changes, wysiwyg_tooltip_en };
        }
        onFieldChange(changes);
    }, [wysiwygDisplayVariant]);

    const onFieldChange = (value: Partial<ITemplateExternalFormComponent>) => {
        onChange({
            ...component,
            ...value
        });
    };

    return <div className={styles.container}>
        <div className={styles.description}>{t("externalFormComponentDescription")}</div>
        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>
        <FormLabel title={<div className={styles.descriptionTitle}>
            <div>{t("descriptionVariant")}</div>
            <ComponentTooltip tooltip={{
                useWysiwygTooltip: true,
                wysiwygTooltip: t("formDescriptionTooltip")
            }} id="template_external_form_tooltip"
                              className={styles.descriptionTooltip}/>
        </div>}>
            <ComponentRadioGroup
                value={wysiwygDisplayVariant}
                options={[
                    {
                        value: WysiwygDisplayVariant.left,
                        text: t("instructionLeft"),
                        icon: <LeftPanelIcon/>
                    },
                    {
                        value: WysiwygDisplayVariant.tooltip,
                        text: t("instructionTooltip"),
                        icon: <ChatIcon/>
                    },
                    {
                        value: WysiwygDisplayVariant.right,
                        text: t("instructionRight"),
                        icon: <RightPanelIcon/>
                    }
                ]}
                onChange={(value) => setWysiwygDisplayVariant(value)}
            />
        </FormLabel>
        <div className={styles.wysiwygInstruction} dangerouslySetInnerHTML={{ __html: t("imageWysiwygInstruction") }}/>
        <WysiwygEditor
            id="component_wysiwyg_tooltip"
            onChange={(value) => onFieldChange({
                wysiwyg_tooltip: JSON.stringify({
                    ...value,
                    wysiwygDisplayVariant
                })
            })}
            initialValue={stringToWysiwygSettings(component.wysiwyg_tooltip)}
        />
        {enLanguageEnabled && <FormLabel title={t("descriptionEn")}>
            <WysiwygEditor
                id="component_wysiwyg_tooltip_en"
                onChange={(value) => onFieldChange({
                    wysiwyg_tooltip_en: JSON.stringify({
                        ...value,
                        wysiwygDisplayVariant
                    })
                })}
                initialValue={stringToWysiwygSettings(component.wysiwyg_tooltip_en)}
            />
        </FormLabel>}
    </div>;
});
